import { useCreateManyWorkOrderCommission } from '@/core/domain/workorder/WorkOrdersCommisions/WorkOrdersCommissions.hooks';
import { AddManyWorkOrderCommissionState } from '@/core/domain/workorder/WorkOrdersCommisions/WorkOrdersCommissions.types';
import { useUpdateWorkOrderComplement } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import { useDefaultFormErrorNotification } from '@/hooks';
import { Form, FormInstance } from 'antd';
import { FormChangeInfo } from 'rc-field-form/es/FormContext';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { WorkOrderComplementFormData } from '../Complements/ComplementForm';
import { useWorkorderDetailContext } from '../WorkorderDetailProvider';
import handlerCommissionsValues from './handlers/commissions';
import { handlerWorkorderGenarlInfoValues } from './handlers/generalInfo';
import { removeMask } from '@/utils/mask';

export interface Forms {
  generalInfo?: FormInstance<WorkOrderComplementFormData>;
  commissions?: FormInstance<AddManyWorkOrderCommissionState>;
}

interface FormsValues {
  generalInfo?: WorkOrderComplementFormData;
  commissions?: AddManyWorkOrderCommissionState;
}

interface WorkOrderFormContextProps {
  forms: Forms;
  formsValues: FormsValues;
  submitAllForms: VoidFunction;
  isLoading: boolean;
  hasFormChange: boolean;
}

const WorkOrderFormContext = createContext({} as WorkOrderFormContextProps);

const WorkOrderFormProvider: React.FC<PropsWithChildren<unknown>> = (props) => {
  const [forms, setForms] = useState<Forms>({});
  const [formsValues, setFormsValues] = useState<FormsValues>({});
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const {
    workorder: { data: workorder },
    complement: { data: complement, dataUpdatedAt },
  } = useWorkorderDetailContext();

  useEffect(() => {
    if (forms.generalInfo || formsValues.generalInfo) {
      setForms((state) => ({ ...state, generalInfo: undefined }));
      setFormsValues((state) => ({ ...state, generalInfo: undefined }));
    }
  }, [dataUpdatedAt]);

  const workorderUpdate = useUpdateWorkOrderComplement();
  const createManyCommission = useCreateManyWorkOrderCommission();

  const services = {
    generalInfo: workorderUpdate.mutateAsync,
    commissions: createManyCommission.mutateAsync,
  };

  const hasFormChange = Object.values(formsValues?.generalInfo || {}).some(
    (value) => !!value,
  );

  function handleFormChange(name: string, info: FormChangeInfo) {
    if (name && info?.forms[name]) {
      setForms((state) => ({
        ...state,
        [name]: info.forms[name],
      }));
      setFormsValues((state) => ({
        ...state,
        [name]: info.forms[name]?.getFieldsValue(),
      }));
    }
  }

  async function submitAllForms() {
    try {
      await Promise.all(
        Object.keys(forms).map((key: unknown) =>
          forms[key as keyof Forms]?.validateFields(),
        ),
      );

      if (formsValues.generalInfo && workorder && complement) {
        handlerWorkorderGenarlInfoValues(
          formsValues.generalInfo,
          async (data) =>
            await services.generalInfo({
              ...data,
              workOrderId: workorder.id,
              complementId: complement.id,
              contact: removeMask(data.contact),
            }),
        );

        setFormsValues({ ...formsValues, generalInfo: undefined });
      }

      if (formsValues.commissions && complement) {
        handlerCommissionsValues(
          { ...formsValues.commissions, complementId: complement.id },
          (data) => services.commissions(data),
        );

        setFormsValues({ ...formsValues, commissions: undefined });
      }
    } catch (error) {
      sendFormErrorNotification(error);
    }
  }

  return (
    <WorkOrderFormContext.Provider
      value={{
        forms,
        formsValues,
        submitAllForms,
        isLoading: workorderUpdate.isLoading || createManyCommission.isLoading,
        hasFormChange,
      }}
    >
      <Form.Provider onFormChange={handleFormChange}>
        {props.children}
      </Form.Provider>
    </WorkOrderFormContext.Provider>
  );
};

export function useWorkorderForms() {
  return useContext(WorkOrderFormContext);
}

export default WorkOrderFormProvider;
